import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TypographyComponent } from '@purolator/ui-kit-angular';
import type { ShippingDocumentStatusResponse } from '../../core/api/shipping-documents/models/shipping-documents.model';
import { ShippingDocumentsApiService } from '../../core/api/shipping-documents/shipping-documents-api.service';
import { ShipmentApiService } from '../../core/api/shipment/shipment-api.service';
import { resolveCancelErrorI18nKey } from '../../core/api/shipment/models/cancel-shipment.model';
import type { CompletedShipmentConfirmation } from '../../core/api/shipment/models/completed-shipment.model';
import {
  formatShipFromLocalDate,
  isCancelShipmentAvailable,
  isValidCompletedShipment,
  readShipFromProvinceCode,
} from '../../core/api/shipment/models/completed-shipment.model';
import { AnalyticsService } from '../../core/integrations/analytics';
import { TranslatePipe, TranslationService } from '../../core/i18n';
import { ShipmentFlowStateService } from '../../core/services/shipment-flow-state.service';
import { environment } from '../../../environments/environment';
import { AlertToast } from '../../shared/components/alert-toast/alert-toast';
import { readPageSkeletonPreviewQueryFlag } from '../../shared/utils/e2e-query-flags';
import { DelayedLoadingController } from '../../shared/utils/delayed-loading';
import { resolveEndOfLocalDayUtcMs } from '../../shared/utils/ship-from-local-clock';
import { ShipmentStepper } from '../ship-from-ship-to/sections/shipment-stepper/shipment-stepper';
import { buildGetLabelsSummaryViews } from './utils/build-get-labels-summary-views';
import { hasAssociatedPickup as shipmentHasAssociatedPickup } from './utils/has-associated-pickup';
import { GetLabelsActions } from './sections/get-labels-actions/get-labels-actions';
import { GetLabelsCancelShipmentDialog } from './sections/get-labels-cancel-shipment-dialog/get-labels-cancel-shipment-dialog';
import { GetLabelsCancelledShipmentDialog } from './sections/get-labels-cancelled-shipment-dialog/get-labels-cancelled-shipment-dialog';
import {
  GetLabelsDetailsToggle,
  type ShipmentDetailsDownloadState,
} from './sections/get-labels-details-toggle/get-labels-details-toggle';
import { GetLabelsEstimatedCosts } from './sections/get-labels-estimated-costs/get-labels-estimated-costs';
import {
  GetLabelsLabelReady,
  type LabelGenerationState,
} from './sections/get-labels-label-ready/get-labels-label-ready';
import { GetLabelsShipmentDetails } from './sections/get-labels-shipment-details/get-labels-shipment-details';
import { GetLabelsSkeleton } from './sections/get-labels-skeleton/get-labels-skeleton';

/** Poll interval so Cancel disappears after 23:59 without a page refresh (OM-69 AC2). */
export const CANCEL_DEADLINE_TICK_MS = 30_000;

/** Delay between Pending status polls while waiting for label document readiness (OM-738). */
export const LABEL_STATUS_POLL_INTERVAL_MS = 1_500;

/** Max status attempts (including the first) before treating Pending as failed (OM-738). */
export const LABEL_STATUS_POLL_MAX_ATTEMPTS = 20;

/** Minimum time to keep the popup loading state visible before showing a result. */
export const LABEL_MINIMUM_LOADING_DURATION_MS = 2_500;

/**
 * Resolves after the given delay. Extracted for unit tests with fake timers.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Create-shipment Get Labels confirmation step (step 5 / OM-66, OM-67, OM-69).
 * Prefetches the label URL on entry, retains click-time retrieval for pending labels, and supports same-day cancel.
 */
@Component({
  selector: 'app-get-labels',
  imports: [
    AlertToast,
    GetLabelsActions,
    GetLabelsCancelShipmentDialog,
    GetLabelsCancelledShipmentDialog,
    GetLabelsDetailsToggle,
    GetLabelsEstimatedCosts,
    GetLabelsLabelReady,
    GetLabelsShipmentDetails,
    GetLabelsSkeleton,
    ShipmentStepper,
    TranslatePipe,
    TypographyComponent,
  ],
  templateUrl: './get-labels.html',
  styleUrl: './get-labels.scss',
})
export class GetLabels implements OnInit {
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly shippingDocumentsApi = inject(ShippingDocumentsApiService);
  private readonly shipmentApi = inject(ShipmentApiService);
  private readonly analytics = inject(AnalyticsService);
  private readonly translation = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private retrievalId = 0;
  private readonly readyLabelDocumentUrl = signal<string | null>(null);

  protected readonly activeStepIndex = 4;
  /** True when the delayed page skeleton should paint (OM-711). */
  protected readonly showPageSkeleton = signal(false);
  private readonly delayedInitialLoading = new DelayedLoadingController((show) => {
    this.showPageSkeleton.set(show);
  });
  protected readonly detailsExpanded = signal(false);
  protected readonly confirmation = signal<CompletedShipmentConfirmation | null>(null);
  protected readonly labelState = signal<LabelGenerationState>('ready');
  protected readonly shipmentDetailsDownloadState = signal<ShipmentDetailsDownloadState>('ready');
  private readonly documentOpenFailed = signal(false);
  protected readonly hasLabelError = computed(
    () => this.labelState() === 'failed' || this.documentOpenFailed(),
  );
  protected readonly hasShipmentDetailsError = computed(
    () => this.shipmentDetailsDownloadState() === 'failed',
  );
  private readonly flowState = signal(this.shipmentFlowState.load());
  protected readonly cancelDialogOpen = signal(false);
  protected readonly cancelledDialogOpen = signal(false);
  protected readonly cancelSubmitting = signal(false);
  /**
   * True when POST /shipments/cancel reported a linked pickup was cancelled (OM-890).
   * Used with session pickup detection so the success dialog stays accurate if soft-fail mapping gaps exist.
   */
  protected readonly pickupCancelledOnCancel = signal(false);
  /** True when cancel confirm should use pickup messaging (OM-890 / ESN parity). */
  protected readonly hasAssociatedPickup = computed(() =>
    shipmentHasAssociatedPickup(this.confirmation()),
  );
  /** i18n key for cancel failure toast; null when no error. */
  protected readonly cancelErrorKey = signal<string | null>(null);
  protected readonly nowTick = signal(Date.now());

  /**
   * True until session confirmation is restored (or redirect). Moneris/create completed on Review.
   * `?pageSkeletonPreview=1` keeps pending true for E2E/QA skeleton a11y scans only.
   */
  private readonly initialContentPending = computed(
    () => readPageSkeletonPreviewQueryFlag() || this.confirmation() === null,
  );

  protected readonly cancelAvailable = computed(() => {
    this.nowTick();
    const completed = this.confirmation();
    return completed ? isCancelShipmentAvailable(completed) : false;
  });

  constructor() {
    effect(() => {
      this.delayedInitialLoading.setPending(this.initialContentPending());
    });
    this.destroyRef.onDestroy(() => this.delayedInitialLoading.destroy());
  }

  /**
   * Loads the completed-shipment confirmation and prefetches label status once.
   */
  ngOnInit(): void {
    const state = this.shipmentFlowState.load();
    const completed = state?.completedShipment;
    if (!isValidCompletedShipment(completed)) {
      this.delayedInitialLoading.fail();
      this.router.navigateByUrl('/').catch(() => undefined);
      return;
    }

    this.flowState.set(state);
    this.confirmation.set(completed);
    this.labelState.set('ready');
    this.analytics.trackEvent({ event: 'ConfirmationPageInit' });
    this.startCancelDeadlineWatch(completed);
    if (!completed.cancelled) {
      void this.prefetchReadyLabelDocument(completed);
    }
  }

  /**
   * Checks status once on entry so an already-generated document can open directly on click.
   * Pending responses and errors intentionally leave the existing click-time retrieval path in
   * place; no polling or user-facing error is triggered during this opportunistic check.
   */
  private async prefetchReadyLabelDocument(
    completed: CompletedShipmentConfirmation,
  ): Promise<void> {
    const documentId = completed.labelDocument?.documentId?.trim();
    if (!documentId) {
      return;
    }

    try {
      const response = await this.shippingDocumentsApi.status({
        pin: completed.trackingPin,
        documentId,
      });
      if (
        this.destroyRef.destroyed ||
        response.documentId !== documentId ||
        response.status.trim().toLowerCase() !== 'completed' ||
        !this.isUsableDocumentUrl(response.docURL)
      ) {
        return;
      }

      this.readyLabelDocumentUrl.set(response.docURL);
    } catch {
      this.readyLabelDocumentUrl.set(null);
    }
  }

  /**
   * Keeps cancel availability in sync with the wall clock while Get Labels stays open.
   * Schedules the hide tick from Ship From end-of-day (OM-891), not browser-local Date.parse.
   */
  private startCancelDeadlineWatch(completed: CompletedShipmentConfirmation): void {
    const tick = (): void => {
      this.nowTick.set(Date.now());
    };

    const intervalId = window.setInterval(tick, CANCEL_DEADLINE_TICK_MS);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));

    const deadlineMs = resolveEndOfLocalDayUtcMs(completed.shipmentCreationDate, {
      provinceCode: readShipFromProvinceCode(completed),
    });
    if (Number.isNaN(deadlineMs)) {
      return;
    }

    const msUntilHide = deadlineMs - Date.now() + 1;
    if (msUntilHide <= 0 || msUntilHide > 36 * 60 * 60 * 1000) {
      return;
    }

    const timeoutId = window.setTimeout(tick, msUntilHide);
    this.destroyRef.onDestroy(() => clearTimeout(timeoutId));
  }

  protected readonly summaryViews = computed(() =>
    buildGetLabelsSummaryViews(
      this.flowState(),
      this.confirmation(),
      this.translation,
      environment.homeLocatorBaseUrl,
    ),
  );
  protected readonly paymentDisplay = computed(() => this.confirmation()?.paymentDisplay ?? null);

  /**
   * Returns whether a document URL can be safely opened as an HTTP(S) resource.
   */
  private isUsableDocumentUrl(documentUrl: string | null): documentUrl is string {
    if (!documentUrl) {
      return false;
    }

    try {
      const parsed = new URL(documentUrl);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  }

  /**
   * Opens a blank tab synchronously so the Shipment Details view stays inside the user gesture.
   * Clears opener for equivalent protection to noopener without treating a null return as failure.
   */
  private openBlankDocumentTab(): Window | null {
    try {
      const popup = window.open('about:blank', '_blank');
      if (!popup) {
        return null;
      }
      popup.opener = null;
      return popup;
    } catch {
      return null;
    }
  }

  /**
   * Opens an already-completed label URL directly from the View/Download click gesture.
   */
  private openReadyLabelDocument(documentUrl: string): boolean {
    try {
      const popup = window.open(documentUrl, '_blank');
      if (!popup) {
        return false;
      }

      popup.opener = null;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Builds the label tab's loading screen before navigating to the completed PDF.
   */
  private createLabelPopupLoadingState(popup: Window): void {
    const popupDocument = popup.document;
    popupDocument.documentElement.lang = this.translation.locale();
    popupDocument.title = this.translation.translate('createShipment.getLabels.labelOpeningTitle');

    const style = popupDocument.createElement('style');
    style.textContent = `
      html, body { width: 100%; height: 100%; margin: 0; }
      .label-document-loading {
        align-items: center; background: rgb(255 255 255 / 85%); border: 0; display: flex;
        flex-direction: column; gap: 16px; inset: 0; justify-content: center; margin: 0;
        padding: 24px; position: fixed; text-align: center; z-index: 1;
      }
      .label-document-loading[hidden] { display: none; }
      .label-document-loading__loader {
        animation: label-document-loader-spin 1s infinite linear; aspect-ratio: 1;
        background: #001996; border-radius: 50%; padding: 8px; width: 50px;
        --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
        -webkit-mask: var(--_m); mask: var(--_m); -webkit-mask-composite: source-out;
        mask-composite: subtract;
      }
      .label-document-loading__message {
        color: #1b1b1b; font-family: Arial, sans-serif; font-size: 16px; line-height: 1.5;
        max-width: 32rem;
      }
      @keyframes label-document-loader-spin { to { transform: rotate(1turn); } }
      @media (prefers-reduced-motion: reduce) {
        .label-document-loading__loader { animation-duration: 3s; }
      }
    `;

    const main = popupDocument.createElement('main');
    main.setAttribute('aria-busy', 'true');
    const overlay = popupDocument.createElement('output');
    overlay.className = 'label-document-loading';
    overlay.setAttribute('aria-live', 'polite');
    overlay.dataset['testid'] = 'label-document-loading';
    const status = popupDocument.createElement('span');
    status.className = 'label-document-loading__message';
    status.textContent = this.translation.translate('createShipment.getLabels.labelOpeningMessage');
    const spinner = popupDocument.createElement('span');
    spinner.className = 'label-document-loading__loader';
    spinner.setAttribute('role', 'progressbar');
    spinner.setAttribute(
      'aria-label',
      this.translation.translate('createShipment.getLabels.creatingLabel'),
    );

    overlay.append(spinner, status);
    popupDocument.head.append(style);
    popupDocument.body.replaceChildren(main, overlay);
  }

  /**
   * Keeps the popup loading state visible for a minimum duration measured from its creation.
   */
  private async waitForMinimumLabelLoadingDuration(startedAt: number): Promise<void> {
    const elapsed = Date.now() - startedAt;
    const remaining = LABEL_MINIMUM_LOADING_DURATION_MS - elapsed;
    if (remaining > 0) {
      await delay(remaining);
    }
  }

  /**
   * Polls POST /shipping-documents/status until Completed, Failure, or attempts are exhausted.
   * Returns the Completed response, or null when Failure / timeout / superseded retrieval.
   */
  private async waitForCompletedLabelDocument(
    pin: string,
    documentId: string,
    retrievalId: number,
  ): Promise<ShippingDocumentStatusResponse | null> {
    for (let attempt = 0; attempt < LABEL_STATUS_POLL_MAX_ATTEMPTS; attempt++) {
      if (retrievalId !== this.retrievalId) {
        return null;
      }

      const response = await this.shippingDocumentsApi.status({ pin, documentId });
      if (retrievalId !== this.retrievalId) {
        return null;
      }

      const normalized = response.status.trim().toLowerCase();
      if (normalized === 'completed') {
        return response;
      }
      if (normalized === 'failure') {
        return null;
      }
      if (normalized !== 'pending') {
        return null;
      }
      if (attempt >= LABEL_STATUS_POLL_MAX_ATTEMPTS - 1) {
        return null;
      }

      await delay(LABEL_STATUS_POLL_INTERVAL_MS);
    }

    return null;
  }

  /**
   * Fetches shipping-label document status and opens the PDF only after it is Completed.
   * The View/Download button stays in its processing state while status is pending.
   * Publishes ConfirmationLabelDownload only after the label PDF tab opens successfully.
   */
  protected async onLabelAction(): Promise<void> {
    if (this.labelState() === 'processing') {
      return;
    }

    this.documentOpenFailed.set(false);
    const readyDocumentUrl = this.readyLabelDocumentUrl();
    if (readyDocumentUrl && this.isUsableDocumentUrl(readyDocumentUrl)) {
      if (!this.openReadyLabelDocument(readyDocumentUrl)) {
        this.documentOpenFailed.set(true);
        return;
      }
      this.analytics.trackEvent({ event: 'ConfirmationLabelDownload' });
      return;
    }

    const completed = this.confirmation();
    const documentId = completed?.labelDocument?.documentId?.trim();
    if (!completed || !documentId) {
      this.labelState.set('failed');
      return;
    }

    const popup = this.openBlankDocumentTab();
    if (!popup) {
      this.documentOpenFailed.set(true);
      this.labelState.set('ready');
      return;
    }

    try {
      this.createLabelPopupLoadingState(popup);
    } catch {
      popup.close();
      this.documentOpenFailed.set(true);
      this.labelState.set('ready');
      return;
    }

    const loadingStartedAt = Date.now();
    const retrievalId = ++this.retrievalId;
    this.labelState.set('processing');

    try {
      const response = await this.waitForCompletedLabelDocument(
        completed.trackingPin,
        documentId,
        retrievalId,
      );
      if (retrievalId !== this.retrievalId) {
        popup.close();
        return;
      }

      if (!response || !this.isUsableDocumentUrl(response.docURL)) {
        await this.waitForMinimumLabelLoadingDuration(loadingStartedAt);
        popup.close();
        this.labelState.set('failed');
        return;
      }

      try {
        await this.waitForMinimumLabelLoadingDuration(loadingStartedAt);
        if (popup.closed) {
          throw new Error('Label popup was closed before the document opened');
        }
        popup.location.href = response.docURL;
      } catch {
        popup.close();
        this.documentOpenFailed.set(true);
        this.labelState.set('ready');
        return;
      }
      this.analytics.trackEvent({ event: 'ConfirmationLabelDownload' });
      this.labelState.set('ready');
    } catch {
      await this.waitForMinimumLabelLoadingDuration(loadingStartedAt);
      popup.close();
      if (retrievalId !== this.retrievalId) {
        return;
      }
      this.labelState.set('failed');
    }
  }

  /**
   * Opens the Shipment Details print view in a new tab. The print page reads the same session
   * data as Get Labels and renders it formatted for the browser's native print dialog, so no
   * backend document request is needed for this action.
   */
  protected onShipmentDetailsDownload(): void {
    const popup = this.openBlankDocumentTab();
    if (!popup) {
      this.shipmentDetailsDownloadState.set('failed');
      return;
    }

    popup.location.href = this.router.serializeUrl(
      this.router.createUrlTree(['/shipment-details-print']),
    );
    this.shipmentDetailsDownloadState.set('ready');
    this.analytics.trackEvent({ event: 'ConfirmationPagePrint' });
  }

  /**
   * Expands or collapses the shipment-details panel.
   */
  protected onDetailsExpandedChange(expanded: boolean): void {
    this.detailsExpanded.set(expanded);
  }

  /**
   * Opens the OM-69 cancel confirmation dialog.
   * Defers open so the triggering click does not immediately dismiss via backdrop.
   */
  protected onCancelShipmentRequested(): void {
    if (!this.cancelAvailable() || this.cancelSubmitting()) {
      return;
    }
    this.cancelErrorKey.set(null);
    queueMicrotask(() => {
      this.cancelDialogOpen.set(true);
    });
  }

  /**
   * Closes the cancel dialog without submitting.
   */
  protected onCancelShipmentDismissed(): void {
    if (this.cancelSubmitting()) {
      return;
    }
    this.cancelDialogOpen.set(false);
  }

  /**
   * Submits POST /shipments/cancel and updates Get Labels only after confirmed success.
   *
   * Session-only console seeds (PIN in sessionStorage without a DynamoDB row) return 404 —
   * that is expected. A real wizard create is required for cancel to succeed.
   */
  protected async onCancelShipmentConfirmed(): Promise<void> {
    const completed = this.confirmation();
    if (!completed || this.cancelSubmitting()) {
      return;
    }

    this.cancelSubmitting.set(true);
    this.cancelErrorKey.set(null);
    this.pickupCancelledOnCancel.set(false);

    try {
      const result = await this.shipmentApi.cancel({
        trackingPin: completed.trackingPin,
        localDate: formatShipFromLocalDate(completed),
      });

      this.pickupCancelledOnCancel.set(Boolean(result.pickupCancelled));
      const updated: CompletedShipmentConfirmation = {
        ...completed,
        cancelled: true,
        cancelledAt: result.cancelledAt,
        shipmentCreationDate: result.shipmentCreationDate || completed.shipmentCreationDate,
        trackingPin: result.trackingPin || completed.trackingPin,
      };
      this.shipmentFlowState.saveCompletedShipment(updated);
      this.confirmation.set(updated);
      this.cancelDialogOpen.set(false);
      this.nowTick.set(Date.now());
      this.analytics.trackEvent({ event: 'ConfirmationPageCancel' });
      queueMicrotask(() => {
        this.cancelledDialogOpen.set(true);
      });
    } catch (error) {
      // Real API failures only — never present as cancelled success.
      this.cancelErrorKey.set(resolveCancelErrorI18nKey(error));
      this.cancelDialogOpen.set(false);
      this.nowTick.set(Date.now());
    } finally {
      this.cancelSubmitting.set(false);
    }
  }

  /**
   * Clears wizard + confirmation session and starts a new shipment flow (OM-66 AC6).
   */
  protected onCreateNewShipment(): void {
    this.analytics.trackEvent({ event: 'ConfirmationPageHome' });
    this.shipmentFlowState.clear();
    this.router.navigateByUrl('/').catch(() => undefined);
  }

  /**
   * Leaves Get Labels after a confirmed cancel (Figma Back to Home).
   */
  protected onCancelledBackToHome(): void {
    this.cancelledDialogOpen.set(false);
    this.onCreateNewShipment();
  }
}
